import { useEffect, useState } from "react";
import { Gift, Plus, Trash2, Save, Pencil, X, Power, RotateCw, Coins, Sparkles, ShieldCheck, Tag, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const BLANK = {
  name: "", sub_title: "", description: "", bonus_pct: 0, cap: 0, min_deposit: 0,
  first_deposit_only: false, turnover_multiplier: 0, code: "", start_at: "", end_at: "",
  enabled: true, order: 0,
};

const num = (v) => (v === "" || v === null || v === undefined ? 0 : parseFloat(v) || 0);

export default function Promotions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode:'create'|'edit', form}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/promotions");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setModal({ mode: "create", form: { ...BLANK, order: rows.length } });
  const openEdit = (p) => setModal({ mode: "edit", id: p.id, form: { ...BLANK, ...p } });
  const setF = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch } }));

  const save = async () => {
    const f = modal.form;
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: f.name.trim(), sub_title: f.sub_title || "", description: f.description || "",
      bonus_pct: num(f.bonus_pct), cap: num(f.cap), min_deposit: num(f.min_deposit),
      turnover_multiplier: num(f.turnover_multiplier), first_deposit_only: !!f.first_deposit_only,
      code: f.code || "", start_at: f.start_at || "", end_at: f.end_at || "",
      enabled: !!f.enabled, order: num(f.order),
    };
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await api.post("/promotions", payload);
        toast.success("Promotion created");
      } else {
        await api.patch(`/promotions/${modal.id}`, payload);
        toast.success("Promotion updated");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/promotions/${p.id}`, { enabled: !p.enabled });
      setRows((r) => r.map((x) => (x.id === p.id ? { ...x, enabled: !p.enabled } : x)));
      toast.success(!p.enabled ? "Promotion enabled" : "Promotion disabled");
    } catch { toast.error("Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/promotions/${p.id}`);
      setRows((r) => r.filter((x) => x.id !== p.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-promotions-page">
      <PageHeader title="Promotions" subtitle="Create & manage deposit bonuses shown on the player deposit page" icon={Gift} />

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">{rows.length} promotion{rows.length !== 1 ? "s" : ""}</div>
        <button
          onClick={openCreate}
          data-testid="promo-add-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm transition-colors"
        >
          <Plus size={16} /> Add promotion
        </button>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-36 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-16 text-center">
          <Gift size={30} className="mx-auto text-gray-300 mb-3" />
          <div className="text-gray-500 font-medium">No promotions yet</div>
          <button onClick={openCreate} className="mt-3 inline-flex items-center gap-1.5 text-emerald-600 font-semibold text-sm"><Plus size={15} /> Create your first promotion</button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((p) => (
            <div key={p.id} data-testid={`promo-card-${p.id}`} className={`relative rounded-xl border bg-white p-4 transition-shadow hover:shadow-md ${p.enabled ? "border-gray-200" : "border-gray-200 opacity-70"}`}>
              {p.bonus_pct > 0 && (
                <div className="absolute top-0 right-0 px-3 py-1.5 rounded-bl-xl rounded-tr-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-extrabold text-sm">{p.bonus_pct}%</div>
              )}
              <div className="flex items-start justify-between gap-2 pr-12">
                <div className="min-w-0">
                  <div className="font-bold text-gray-900 leading-snug truncate">{p.name}</div>
                  {(p.sub_title || p.description) && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{p.sub_title || p.description}</div>}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.min_deposit > 0 && <Chip icon={Coins} tone="emerald">Min ৳{p.min_deposit}</Chip>}
                {p.cap > 0 && <Chip icon={Sparkles} tone="emerald">Up to ৳{p.cap}</Chip>}
                {p.turnover_multiplier > 0 && <Chip icon={RotateCw} tone="amber">{p.turnover_multiplier}x turnover</Chip>}
                {p.first_deposit_only && <Chip icon={ShieldCheck} tone="sky">First deposit</Chip>}
                {p.code && <Chip icon={Tag} tone="gray">{p.code}</Chip>}
                <Chip icon={ArrowUpDown} tone="gray">#{p.order}</Chip>
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${p.enabled ? "text-emerald-600" : "text-gray-400"}`}>
                  <span className={`h-2 w-2 rounded-full ${p.enabled ? "bg-emerald-500" : "bg-gray-300"}`} />
                  {p.enabled ? "Active" : "Disabled"}
                </span>
                <div className="flex items-center gap-1">
                  <IconBtn onClick={() => toggle(p)} title={p.enabled ? "Disable" : "Enable"} testId={`promo-toggle-${p.id}`}><Power size={15} className={p.enabled ? "text-emerald-600" : "text-gray-400"} /></IconBtn>
                  <IconBtn onClick={() => openEdit(p)} title="Edit" testId={`promo-edit-${p.id}`}><Pencil size={15} className="text-gray-600" /></IconBtn>
                  <IconBtn onClick={() => remove(p)} title="Delete" testId={`promo-delete-${p.id}`}><Trash2 size={15} className="text-red-500" /></IconBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center sm:justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh]" data-testid="promo-modal">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
              <Gift size={18} className="text-emerald-600" />
              <h3 className="font-bold text-gray-900">{modal.mode === "create" ? "New promotion" : "Edit promotion"}</h3>
              <button onClick={() => setModal(null)} className="ml-auto h-8 w-8 grid place-items-center rounded-full hover:bg-gray-100 text-gray-500"><X size={18} /></button>
            </div>

            <div className="overflow-y-auto px-5 py-4 space-y-4">
              <Field label="Name" required><input value={modal.form.name} onChange={(e) => setF({ name: e.target.value })} placeholder="51% First Deposit Bonus" className={inp} data-testid="promo-f-name" /></Field>
              <Field label="Subtitle"><input value={modal.form.sub_title} onChange={(e) => setF({ sub_title: e.target.value })} placeholder="প্রথম ডিপোজিটে ৫১% বোনাস · ৫০x টার্নওভার" className={inp} data-testid="promo-f-subtitle" /></Field>
              <Field label="Description"><textarea value={modal.form.description} onChange={(e) => setF({ description: e.target.value })} rows={2} className={inp} data-testid="promo-f-desc" /></Field>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Bonus %"><input type="number" step="0.1" value={modal.form.bonus_pct} onChange={(e) => setF({ bonus_pct: e.target.value })} className={inp} data-testid="promo-f-bonus" /></Field>
                <Field label="Turnover (x)"><input type="number" step="0.1" value={modal.form.turnover_multiplier} onChange={(e) => setF({ turnover_multiplier: e.target.value })} placeholder="50" className={inp} data-testid="promo-f-turnover" /></Field>
                <Field label="Min deposit ৳"><input type="number" step="1" value={modal.form.min_deposit} onChange={(e) => setF({ min_deposit: e.target.value })} placeholder="200" className={inp} data-testid="promo-f-min" /></Field>
                <Field label="Max bonus ৳ (cap)"><input type="number" step="1" value={modal.form.cap} onChange={(e) => setF({ cap: e.target.value })} className={inp} data-testid="promo-f-cap" /></Field>
                <Field label="Promo code"><input value={modal.form.code} onChange={(e) => setF({ code: e.target.value })} className={inp} data-testid="promo-f-code" /></Field>
                <Field label="Order"><input type="number" value={modal.form.order} onChange={(e) => setF({ order: e.target.value })} className={inp} data-testid="promo-f-order" /></Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Starts (optional)"><input type="datetime-local" value={modal.form.start_at} onChange={(e) => setF({ start_at: e.target.value })} className={inp} data-testid="promo-f-start" /></Field>
                <Field label="Ends (optional)"><input type="datetime-local" value={modal.form.end_at} onChange={(e) => setF({ end_at: e.target.value })} className={inp} data-testid="promo-f-end" /></Field>
              </div>

              <div className="flex flex-wrap gap-4 pt-1">
                <Toggle checked={!!modal.form.first_deposit_only} onChange={(v) => setF({ first_deposit_only: v })} label="First deposit only" testId="promo-f-firstonly" />
                <Toggle checked={!!modal.form.enabled} onChange={(v) => setF({ enabled: v })} label="Enabled (show to players)" testId="promo-f-enabled" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setModal(null)} className="h-10 px-4 rounded-lg text-gray-600 font-semibold text-sm hover:bg-gray-100">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold" data-testid="promo-save-btn">
                <Save size={15} /> {saving ? "Saving…" : modal.mode === "create" ? "Create" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}

function Chip({ icon: Icon, tone, children }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
    gray: "bg-gray-100 text-gray-600",
  };
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md ${tones[tone] || tones.gray}`}>{Icon && <Icon size={11} />}{children}</span>;
}

function IconBtn({ onClick, title, testId, children }) {
  return <button onClick={onClick} title={title} data-testid={testId} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-gray-100 transition-colors">{children}</button>;
}

function Toggle({ checked, onChange, label, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testId} className="inline-flex items-center gap-2.5">
      <span className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </button>
  );
}
